import * as Yup from "yup";
import { getDateAfter, getHyphenedDate } from "../../utils/utils";

/** 適格事業者履歴リストのバリデーションスキーマを作成する */
const createInvoiceRegistrationHistoryListValidationSchema = (historyList) => {
  // 適格事業者履歴　期間にかぶりのある行
  const duplicatedIndexes = detectInvoiceRegistorationHistoryDuplicatedIndexes(historyList);

  return Yup.array().of(
    Yup.object()
      // 登録日が解除日より後にある場合エラー
      .test(
        'registration date is later than cancellation date',
        '登録日が解除日よりも後の日付です。',
        (historyInfo) => {
          return historyInfo.isDeleted
                || !historyInfo.registrationDate || !historyInfo.cancellationDate
                || historyInfo.registrationDate <= historyInfo.cancellationDate;        
        }
      // 期間が他の行と被る場合エラー
      ).test(
        'duplicated histories',
        "期間が他の行と重複しています。",
        (_, ctx) => !duplicatedIndexes.has(ctx.options.index)
      )
  );
}

// 適格事業者履歴　期間に被りのあるリスト番号を検出
const detectInvoiceRegistorationHistoryDuplicatedIndexes = (historyList) => {
  // 各期間のstartとend(の翌日)を順番に並び替える
  const historyDates
    = historyList
      .map((h, idx) => ({...h, listIndex: idx}))
      .filter((h) => !h.isDeleted)
      .map((h) => [
        {listIndex : h.listIndex, date: h.registrationDate, type: "start"},
        {listIndex : h.listIndex, date: h.cancellationDate && getHyphenedDate(getDateAfter(h.cancellationDate, 1)), type: "end"}
      ]).flat()
      // 並び替えの順序は、「"start"かつ日付null」<「"start","end"関係なく日付順」<「"end"かつ日付null」
      .toSorted((p1, p2) => {
        if ((p1.type ==='start' && !p1.date) || (p2.type ==='end' && !p2.date)) {
          return -1;
        }
        if ((p1.type ==='end' && !p1.date) || (p2.type ==='start' && !p2.date)) {
          return 1;
        }
        if ((p1.date < p2.date)) {
          return -1;
        }
        if ((p1.date > p2.date)) {
          return 1;
        }
        return 0;
      });

  // 各startおよびendごとにその時点で区間に含まれる項目を選ぶ
  const datesCount = historyDates.length;
  const indexesAtDate = new Set([]);
  const duplicatedIndexs = new Set([]);
  
  historyDates.forEach((p, idx) => {
    if (p.type === 'start') {
      indexesAtDate.add(p.listIndex);
    } else {
      indexesAtDate.delete(p.listIndex);
    }

    if ((idx >= datesCount - 1 || p.date !== historyDates[idx + 1].date)
      && (indexesAtDate.size > 1)
    ) {
      indexesAtDate.forEach((listIndex) => duplicatedIndexs.add(listIndex));
    }

  });

  return duplicatedIndexs;
}

export const validationSchema = Yup.object({
  commodityOwnerCd: Yup.number()
    .typeError("数値のみ・「-」なし")
    .required("必須項目"),
  commodityOwnerName: Yup.string().required("必須項目"),
  tel: Yup.string()
    .matches(/^[\d-]+$/, "数値と（-）のみ")
    .nullable(true),
  fax: Yup.string()
    .matches(/^[\d-]+$/, "数値と（-）のみ")
    .nullable(true),
  email: Yup.string().email("メールアドレスが不正です").nullable(true),
  salesRecordTypeCd: Yup.number()
    .notOneOf([-1], "必須項目")
    .required("必須項目"),
  commodityOwnerTotalDivisionCd: Yup.number()
    .notOneOf([-1], "必須項目")
    .required("必須項目"),
  feeRate: Yup.number().typeError("数値と「.」のみ").required("必須項目"),
  shippingFee: Yup.number().typeError("数値と「.」のみ").required("必須項目"),
  areaCd: Yup.number().nullable().test(
    "required for local commodity owner",
    "地物荷主は必須",
    (areaCd, ctx) => ctx.parent.salesRecordTypeCd !== 1 || (areaCd !== -1 && areaCd != null)
  ),
  jaCode: Yup.number().typeError("数値のみ").nullable(true),
  accountNumber: Yup.string().matches(/^\d+$/, "数値のみ").nullable("必須項目"),
  invoiceRegistrationHistoryList: Yup.lazy(createInvoiceRegistrationHistoryListValidationSchema),
});
